import React, { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { canComonitor } from 'flow/actions/extension-actions';
import { CallMethodType } from 'flow/utils/call-utils';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import BaseModal from 'flow/components/ui-components/BaseModal';
import Icon from 'flow/components/ui-components/Icon';
import { twMerge } from 'tailwind-merge';
import useAppContext from 'flow/hooks/useAppContext';
import { TypedMap } from 'flow/types/TypedMap';
import { Extension } from 'flow/types/Extension';
import { getStatusOfExtension } from 'flow/selectors/selectors';
import { isExtensionInCall } from 'flow/selectors/call-selectors';
import { extensionStatuses } from 'flow/utils';

interface RingingCall {
    source: string;
    destination: string;
}

interface Props {
    extensionToCall: TypedMap<Extension>;
    onCancel: Function;
    onCall: React.MouseEventHandler<HTMLButtonElement>;
    onNotify: React.MouseEventHandler<HTMLButtonElement>;
    onPickupCall?: React.MouseEventHandler<HTMLButtonElement>;
    onComonitor: React.MouseEventHandler<HTMLButtonElement>;
    allowPresenceNotification: boolean;
    callMethod?: typeof CallMethodType;
    ringingCall?: TypedMap<RingingCall>;
}

const CallBusyExtensionDialog = ({
    extensionToCall,
    onCall,
    onCancel,
    onNotify,
    onPickupCall,
    onComonitor,
    ringingCall,
    callMethod,
    allowPresenceNotification,
}: Props) => {
    const dispatch = useAppDispatch();
    const [canComonitorState, setCanComonitorState] = useState<boolean>(false);
    const { i18next } = useAppContext();
    const landlineCall =
        ringingCall.get('destination') ===
        extensionToCall.getIn(['contact', 'fixedNumber']);

    const disabled =
        callMethod !== CallMethodType.LOCAL &&
        callMethod !== CallMethodType.ALWAYS_ASK;

    const firstName =
        extensionToCall?.getIn(['contact', 'firstName']) ||
        i18next.t('Contact.Unknown');

    const extensionToCallStatus = useAppSelector((state) =>
        getStatusOfExtension(state, extensionToCall),
    );

    const extensionToCallIsInCall = useAppSelector((state) =>
        extensionToCall?.has('key')
            ? isExtensionInCall(state, extensionToCall.get('key'))
            : false,
    );

    const extensionToCallIsInExternalCall =
        extensionToCallStatus === extensionStatuses.inExternalCall.key;

    useEffect(() => {
        if (extensionToCallIsInCall) {
            fetchCanComonitorState();
        }
    }, [extensionToCallIsInCall]);

    const fetchCanComonitorState = async () => {
        try {
            const result = await dispatch(
                canComonitor(extensionToCall.get('key')),
            );
            setCanComonitorState(!!result); // Update the code to handle the asynchronous result correctly
        } catch (error) {
            setCanComonitorState(false);
        }
    };

    const getTitleItem = () => {
        const firstName =
            extensionToCall?.getIn(['contact', 'firstName']) ||
            i18next.t('Contact.Unknown');

        const title = i18next.t(
            extensionToCallStatus === 'ringingCall'
                ? 'General.ExtensionBusyAlet.RingingCall'
                : 'General.ExtensionBusyAlet.Title',
            { firstName },
        );

        return (
            <div className="flex items-center gap-3">
                <ContactAvatar contact={extensionToCall.get('contact')} />
                <div className="lowercase first-letter:uppercase">{title}</div>
            </div>
        );
    };
    const shouldRenderPickCall =
        extensionToCallStatus !== 'ringingCall' || !onPickupCall;

    const shouldUserMonitor =
        canComonitorState &&
        extensionToCallIsInCall &&
        !extensionToCallIsInExternalCall;

    return (
        <BaseModal
            open={true}
            header={getTitleItem()}
            onClose={onCancel}
            disableActions={true}
            onSubmit={null}
        >
            <div className="mb-6">
                <button
                    onClick={onCall}
                    key="call"
                    className="flex w-full cursor-pointer rounded-sm p-2 hover:bg-gray-100"
                >
                    <div className="inline-block rounded-lg bg-gray-200 p-1">
                        <Icon iconKey="call" />
                    </div>
                    <span className="ms-2 self-center text-gray-800">
                        {i18next.t('General.ExtensionBusyAlet.CallAnyway')}
                    </span>
                </button>
                {allowPresenceNotification ? (
                    <button
                        onClick={onNotify}
                        key={'notify'}
                        className="flex w-full cursor-pointer rounded-sm p-2 hover:bg-gray-100"
                    >
                        <div className="inline-block self-start rounded-lg bg-gray-200 p-1">
                            <Icon iconKey="notifications" />
                        </div>
                        <div className="ms-2 flex flex-col self-center text-left text-gray-800">
                            <span>
                                {i18next.t(
                                    'General.ExtensionBusyAlet.NotifyWhenAvailable',
                                )}
                            </span>
                            <span className="text-sm text-gray-500">
                                {i18next.t(
                                    'General.ExtensionBusyAlet.NotificationPermission',
                                )}
                            </span>
                        </div>
                    </button>
                ) : null}
                {!shouldRenderPickCall ? (
                    <button
                        onClick={landlineCall ? onPickupCall : null}
                        key={'pickCall'}
                        className={twMerge(
                            'flex w-full rounded-sm p-2',
                            !landlineCall
                                ? 'opacity-50'
                                : 'cursor-pointer hover:bg-gray-100',
                        )}
                    >
                        <div className="inline-block self-start rounded-lg bg-gray-200 p-1">
                            <Icon iconKey="callMissedOutgoing" />
                        </div>

                        <div className="ms-2 flex flex-col self-center text-left text-gray-800">
                            <span>
                                {i18next.t(
                                    'General.ExtensionBusyAlet.PickCall',
                                )}
                            </span>
                            <span className="text-sm text-gray-500">
                                {landlineCall
                                    ? i18next.t(
                                          'General.ExtensionBusyAlet.PickCall.Description',
                                          { source: ringingCall.get('source') },
                                      )
                                    : i18next.t(
                                          'General.ExtensionBusyAlet.PickCall.MobileInfo',
                                      )}
                            </span>
                        </div>
                    </button>
                ) : null}
                {shouldUserMonitor ? (
                    <button
                        onClick={!disabled ? onComonitor : null}
                        key={'comonitor'}
                        className={twMerge(
                            'flex w-full rounded-sm p-2',
                            disabled
                                ? 'opacity-50'
                                : 'cursor-pointer hover:bg-gray-100',
                        )}
                    >
                        <div className="inline-block self-start rounded-lg bg-gray-200 p-1">
                            <Icon iconKey="hearing" />
                        </div>
                        <div className="ms-2 flex flex-col self-center text-left text-gray-800">
                            <span>{i18next.t('colistening.listen_in')}</span>
                            <span className="text-sm text-gray-500">
                                {disabled
                                    ? i18next.t(
                                          'colistening.listen_in_call_method_info',
                                      )
                                    : i18next.t(
                                          'colistening.listen_in_button_description',
                                          { Name: firstName },
                                      )}
                            </span>
                        </div>
                    </button>
                ) : null}
            </div>
        </BaseModal>
    );
};

export default CallBusyExtensionDialog;
